import React from 'react';
import { Link } from 'react-router-dom';
import { WifiOff, Ban, Wifi, ArrowRight } from 'lucide-react';
import useDashboardTheme from '../hooks/useDashboardTheme';

/**
 * Teknisi Quick Actions — actionable shortcuts shown only when role=teknisi.
 * Theme-aware: adapts to Glass / Light / Dark variants.
 */
const TeknisiQuickActions = ({ stats }) => {
  const { baseVariant } = useDashboardTheme();
  const isDark = baseVariant === 'dark';
  const isGlass = baseVariant === 'glass';

  const totalCustomers = stats?.total_customers ?? 0;
  const online = stats?.pppoe_online ?? 0;
  const offline = Math.max(totalCustomers - online, 0);
  const isolated = stats?.isolated_customers ?? stats?.total_isolated ?? stats?.isolated_count ?? 0;
  const hotspotActive = stats?.hotspot_online ?? stats?.hotspot?.online ?? 0;

  const tiles = [
    { label: 'Pelanggan Offline', sub: 'Perlu pengecekan koneksi', value: offline,
      to: '/app/customers?status=offline', tone: 'amber', icon: WifiOff, testId: 'teknisi-tile-offline' },
    { label: 'Pelanggan Diisolir', sub: 'Cek alasan isolir / unblock', value: isolated,
      to: '/app/customers?isolir=isolated', tone: 'rose', icon: Ban, testId: 'teknisi-tile-isolated' },
    { label: 'Hotspot Aktif', sub: 'Sesi voucher berjalan', value: hotspotActive,
      to: '/app/hotspot-aktif', tone: 'emerald', icon: Wifi, testId: 'teknisi-tile-hotspot' },
  ];

  const tonePalette = {
    light: {
      amber:   { border: 'border-amber-300',   iconBg: 'bg-amber-50 text-amber-600',     hover: 'hover:border-amber-400' },
      rose:    { border: 'border-rose-300',    iconBg: 'bg-rose-50 text-rose-600',       hover: 'hover:border-rose-400' },
      emerald: { border: 'border-emerald-300', iconBg: 'bg-emerald-50 text-emerald-600', hover: 'hover:border-emerald-400' },
      indigo:  { border: 'border-indigo-300',  iconBg: 'bg-indigo-50 text-indigo-600',   hover: 'hover:border-indigo-400' },
    },
    dark: {
      amber:   { border: 'border-amber-500/30',   iconBg: 'bg-amber-500/15 text-amber-300',     hover: 'hover:border-amber-400/60' },
      rose:    { border: 'border-rose-500/30',    iconBg: 'bg-rose-500/15 text-rose-300',       hover: 'hover:border-rose-400/60' },
      emerald: { border: 'border-emerald-500/30', iconBg: 'bg-emerald-500/15 text-emerald-300', hover: 'hover:border-emerald-400/60' },
      indigo:  { border: 'border-indigo-500/30',  iconBg: 'bg-indigo-500/15 text-indigo-300',   hover: 'hover:border-indigo-400/60' },
    },
    glass: {
      amber:   { iconBg: 'bg-amber-500/20 text-amber-700' },
      rose:    { iconBg: 'bg-rose-500/20 text-rose-700' },
      emerald: { iconBg: 'bg-emerald-500/20 text-emerald-700' },
      indigo:  { iconBg: 'bg-indigo-500/20 text-indigo-700' },
    },
  };

  const palette = tonePalette[isDark ? 'dark' : isGlass ? 'glass' : 'light'];

  // Card surface: black on dark, frosted glass on glass, plain white on light
  const cardBase = isDark
    ? 'bg-black/95 shadow-[0_8px_24px_rgba(0,0,0,0.5)]'
    : isGlass
      ? 'shadow-sm border border-white/40'
      : 'bg-white shadow-sm';

  const cardInlineStyle = isGlass
    ? { background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }
    : undefined;

  const labelClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const valueClass = isDark ? 'text-white' : 'text-slate-900';
  const subClass = 'text-slate-500';

  return (
    <div className="bg-slate-50 dark:bg-slate-950 px-3 sm:px-5 lg:px-6 pt-4 pb-2" data-testid="teknisi-quick-actions">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          const tone = palette[t.tone];
          const borderClass = isGlass ? '' : `border ${tone.border} ${tone.hover}`;
          return (
            <Link
              key={t.testId}
              to={t.to}
              data-testid={t.testId}
              style={cardInlineStyle}
              className={`group relative rounded-xl ${borderClass} ${cardBase} p-4 transition-all duration-200 hover:-translate-y-0.5`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tone.iconBg}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[11px] font-semibold uppercase tracking-wider ${labelClass}`}>{t.label}</div>
                  <div className={`text-2xl font-bold mt-0.5 ${valueClass}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {t.value.toLocaleString('id-ID')}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${subClass}`}>{t.sub}</div>
                </div>
                <ArrowRight className={`w-4 h-4 transition-all ${isDark ? 'text-slate-600 group-hover:text-slate-300' : 'text-slate-300 group-hover:text-slate-600'} group-hover:translate-x-0.5`} />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TeknisiQuickActions;
